import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFavorites } from '@/hooks/useFavorites';
import { verifiedProperties, type VerifiedProperty } from '@/mocks/properties';
import { propertyRoute } from '@/mocks/propertyRoutes';

type CompareField = {
  key: keyof VerifiedProperty;
  label: string;
  kind: 'text' | 'bool';
};

const COMPARE_FIELDS: CompareField[] = [
  { key: 'location', label: 'Location', kind: 'text' },
  { key: 'bedrooms', label: 'Bedrooms', kind: 'text' },
  { key: 'bathrooms', label: 'Bathrooms', kind: 'text' },
  { key: 'sleeps', label: 'Sleeps', kind: 'text' },
  { key: 'beachfront', label: 'Beachfront', kind: 'bool' },
  { key: 'privatePool', label: 'Private pool', kind: 'bool' },
  { key: 'elevator', label: 'Private elevator', kind: 'bool' },
  { key: 'gulfViews', label: 'Gulf views', kind: 'bool' },
  { key: 'outdoorLiving', label: 'Outdoor living', kind: 'bool' },
  { key: 'golfCart', label: 'Golf cart', kind: 'bool' },
  { key: 'bikes', label: 'Bikes', kind: 'bool' },
  { key: 'carriageHouse', label: 'Carriage house', kind: 'bool' },
  { key: 'summerKitchen', label: 'Summer kitchen', kind: 'bool' },
];

function CompareModal({ properties, onClose }: { properties: VerifiedProperty[]; onClose: () => void }) {
  const { toggleFavorite } = useFavorites();

  return (
    <div
      className="fixed inset-0 z-[90] bg-foreground-950/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-fade-up"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Compare properties"
    >
      <div
        className="bg-background-50 w-full max-w-4xl max-h-[90vh] rounded-lg overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 md:px-8 py-5 border-b border-background-200/70">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-accent-700 mb-1">Side by side</p>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">Compare properties</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close comparison"
            className="w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-auto subtle-scroll">
          <div className="sm:hidden p-4 space-y-4">
            {properties.map((p) => (
              <div key={p.id} className="border border-background-200/80 rounded-lg overflow-hidden bg-background-50">
                <div className="flex gap-3 p-3 border-b border-background-200/70">
                  <img src={p.image} alt={p.name} className="w-20 h-20 rounded-lg object-cover object-center" />
                  <div className="min-w-0">
                    <Link
                      to={propertyRoute(p.id)}
                      onClick={onClose}
                      className="font-heading text-xl leading-tight text-foreground-950 hover:text-accent-600 transition-colors"
                    >
                      {p.name}
                    </Link>
                    <p className="mt-1 text-sm text-foreground-500">{p.location}</p>
                    <button
                      type="button"
                      onClick={() => toggleFavorite(p.id)}
                      className="mt-2 text-xs text-foreground-500 hover:text-foreground-900 cursor-pointer inline-flex items-center gap-1"
                    >
                      <i className="ri-close-circle-line" />
                      Remove
                    </button>
                  </div>
                </div>
                <Link
                  to={propertyRoute(p.id)}
                  onClick={onClose}
                  className="mx-3 mb-3 inline-flex w-[calc(100%-1.5rem)] items-center justify-center gap-2 rounded-lg bg-foreground-950 px-4 py-3 text-sm text-background-50 transition-colors hover:bg-accent-600"
                >
                  View {p.name}
                  <i className="ri-arrow-right-up-line" />
                </Link>
                <div className="grid grid-cols-3 border-b border-background-200/70">
                  {[
                    ['Bedrooms', p.bedrooms],
                    ['Bathrooms', p.bathrooms],
                    ['Sleeps', p.sleeps],
                  ].map(([label, value]) => (
                    <div key={label} className="px-3 py-3 border-r last:border-r-0 border-background-200/70">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-foreground-400">{label}</p>
                      <p className="mt-1 font-heading text-lg text-foreground-950">{value}</p>
                    </div>
                  ))}
                </div>
                <div className="p-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-accent-700 mb-2">Good To Know</p>
                  <div className="flex flex-wrap gap-2">
                    {p.verifiedAmenityPhrases.slice(0, 7).map((phrase) => (
                      <span
                        key={phrase}
                        className="inline-flex rounded-lg bg-background-100 px-2.5 py-1.5 text-xs leading-snug text-foreground-800"
                      >
                        {phrase}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden sm:block min-w-[560px]">
            <div className="flex sticky top-0 z-10 bg-background-50 border-b border-background-200/70">
              <div className="w-[150px] md:w-[180px] shrink-0" />
              {properties.map((p) => (
                <div
                  key={p.id}
                  className="flex-1 px-3 py-5 text-center flex flex-col items-center gap-2 border-l border-background-200/70"
                >
                  <img src={p.image} alt={p.name} className="w-16 h-16 rounded-full object-cover object-center" />
                  <Link
                    to={propertyRoute(p.id)}
                    onClick={onClose}
                    className="font-heading text-base md:text-lg text-foreground-950 leading-tight hover:text-accent-600 transition-colors cursor-pointer"
                  >
                    {p.name}
                  </Link>
                  <p className="text-xs text-foreground-500">{p.location}</p>
                  <button
                    type="button"
                    onClick={() => toggleFavorite(p.id)}
                    className="text-xs text-foreground-500 hover:text-foreground-900 cursor-pointer inline-flex items-center gap-1 mt-1 whitespace-nowrap"
                  >
                    <i className="ri-close-circle-line" />
                    Remove
                  </button>
                </div>
              ))}
            </div>

            {COMPARE_FIELDS.map((field) => (
              <div key={String(field.key)} className="flex border-b border-background-200/70">
                <div className="w-[150px] md:w-[180px] shrink-0 px-3 py-3.5 text-sm text-foreground-600 flex items-center">
                  {field.label}
                </div>
                {properties.map((p) => {
                  if (field.kind === 'bool') {
                    const confirmed = p[field.key] as boolean;
                    return (
                      <div
                        key={p.id}
                        className="flex-1 px-3 py-3.5 text-sm text-center border-l border-background-200/70 flex items-center justify-center"
                      >
                        {confirmed ? (
                          <span className="inline-flex items-center gap-1.5 text-foreground-900">
                            <i className="ri-check-line text-accent-600" />
                            Yes
                          </span>
                        ) : (
                          <span className="text-foreground-400 italic">Not listed</span>
                        )}
                      </div>
                    );
                  }
                  const display = String(p[field.key]);
                  return (
                    <div
                      key={p.id}
                      className="flex-1 px-3 py-3.5 text-sm text-center border-l border-background-200/70 flex items-center justify-center text-foreground-900"
                    >
                      {display}
                    </div>
                  );
                })}
              </div>
            ))}

            <div className="px-4 md:px-6 py-4 text-xs text-foreground-500 leading-relaxed">
              Use this side-by-side view to compare the details that matter most before choosing your stay.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CompareBar() {
  const { favorites, toggleFavorite } = useFavorites();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const selected = verifiedProperties.filter((p) => favorites.includes(p.id));

  useEffect(() => {
    if (selected.length < 2) setOpen(false);
  }, [selected.length]);

  if (selected.length < 2) return null;

  const onPropertyPage =
    location.pathname === '/the-belvedere' ||
    location.pathname === '/hannahs-pearl' ||
    location.pathname === '/sea-dreams' ||
    location.pathname === '/beach-beach-baby' ||
    location.pathname === '/bella-vita' ||
    location.pathname === '/sunset-bungalow-30a';

  return (
    <>
      <div
        className={`fixed ${onPropertyPage ? 'bottom-28 sm:bottom-24' : 'bottom-4'} left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-[60] animate-fade-up`}
      >
        <div className="mx-auto flex w-full max-w-[22rem] sm:w-auto sm:max-w-none items-center gap-1.5 rounded-lg bg-foreground-950 text-background-50 pl-2 pr-2 py-2 border border-foreground-800 shadow-[0_14px_34px_rgba(0,0,0,0.18)]">
          {selected.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => toggleFavorite(p.id)}
              title={`Remove ${p.name}`}
              aria-label={`Remove ${p.name}`}
              className="relative cursor-pointer"
            >
              <img src={p.image} alt="" className="w-9 h-9 rounded-full object-cover object-center" />
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-background-50 text-foreground-900 flex items-center justify-center">
                <i className="ri-close-line text-[11px]" />
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="ml-1.5 inline-flex min-w-0 flex-1 sm:flex-none items-center justify-center gap-2 text-sm px-3 sm:px-4 py-2 rounded-lg bg-[#EFE4D6] text-foreground-950 hover:bg-[#E9DCCB] transition-colors cursor-pointer whitespace-nowrap"
          >
            Compare {selected.length} properties
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>

      {open && <CompareModal properties={selected} onClose={() => setOpen(false)} />}
    </>
  );
}
